import { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { addFavorite, removeFavorite, checkIsFavorite } from '@/api/favorites';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface FavoriteButtonProps {
  propertyId: string;
  initialIsFavorite?: boolean;
  onFavoriteChange?: (isFavorite: boolean) => void;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export function FavoriteButton({
  propertyId,
  initialIsFavorite = false,
  onFavoriteChange,
  variant = 'ghost',
  size = 'icon'
}: FavoriteButtonProps) {
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!user || !propertyId) return;
      
      try {
        console.log('Checking favorite status for property:', propertyId);
        const isFav = await checkIsFavorite(propertyId);
        console.log('Favorite status result:', isFav);
        setIsFavorite(isFav);
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };

    checkFavoriteStatus();
  }, [propertyId, user]);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.error('Debes iniciar sesión para guardar favoritos', {
        action: {
          label: 'Iniciar sesión',
          onClick: () => navigate('/login')
        }
      });
      return;
    }

    if (!propertyId) {
      console.error('PropertyId is missing:', propertyId);
      toast.error('Error: ID de propiedad no válido');
      return;
    }

    if (isLoading) return;

    setIsLoading(true);
    setIsAnimating(true);
    
    try {
      console.log('Attempting to toggle favorite for property:', propertyId);
      
      if (isFavorite) {
        console.log('Removing from favorites...');
        const result = await removeFavorite(propertyId);
        
        if (result.success) {
          setIsFavorite(false);
          toast.success('Propiedad eliminada de favoritos', {
            description: 'La propiedad ha sido eliminada de tu lista de favoritos',
            icon: <Heart className="h-5 w-5" />,
          });
          
          if (onFavoriteChange) {
            onFavoriteChange(false);
          }
        }
      } else {
        console.log('Adding to favorites...');
        const result = await addFavorite(propertyId.trim());
        
        // Verificar si ya existía o si es un éxito simulado
        if ((result as any).alreadyExists) {
          console.log('Property was already in favorites');
          setIsFavorite(true);
          toast.info('Esta propiedad ya está en tus favoritos');
        } else if ((result as any).simulatedSuccess) {
          console.log('Simulated success for UI');
          setIsFavorite(true);
          toast.success('Propiedad agregada a favoritos', {
            description: 'La propiedad ha sido agregada a tu lista de favoritos',
            icon: <Heart className="h-5 w-5 fill-current text-red-500" />,
            action: {
              label: 'Ver favoritos',
              onClick: () => navigate('/dashboard/favorites')
            }
          });
        } else {
          setIsFavorite(true);
          toast.success('Propiedad agregada a favoritos', {
            description: 'La propiedad ha sido agregada a tu lista de favoritos',
            icon: <Heart className="h-5 w-5 fill-current text-red-500" />,
            action: {
              label: 'Ver favoritos',
              onClick: () => navigate('/dashboard/favorites')
            }
          });
        }
        
        if (onFavoriteChange) {
          onFavoriteChange(true);
        }
      }
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      
      // Manejar errores de servidor (500) de manera especial
      if (error.response?.status >= 500) {
        // Para errores del servidor, actualizar la UI como si hubiera funcionado
        // pero mostrar un mensaje informativo
        if (!isFavorite) {
          setIsFavorite(true); // Actualizar UI como si se hubiera agregado
          toast.warning('La propiedad se ha marcado como favorita, pero hubo un problema en el servidor. Puede que necesites actualizar la página para ver los cambios.', {
            duration: 5000
          });
          
          if (onFavoriteChange) {
            onFavoriteChange(true);
          }
        } else {
          // No cambiar el estado para eliminación con error
          toast.warning('Hubo un problema al actualizar tus favoritos. Por favor, intenta de nuevo más tarde.', {
            duration: 5000
          });
        }
      } else if (error.response?.status === 401) {
        toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', {
          action: {
            label: 'Iniciar sesión',
            onClick: () => navigate('/login')
          }
        });
      } else if (error.response?.status === 400 && error.response?.data?.message?.includes('ya está en favoritos')) {
        // Si ya está en favoritos, actualizar la UI
        setIsFavorite(true);
        toast.info('Esta propiedad ya está en tus favoritos');
        
        if (onFavoriteChange) {
          onFavoriteChange(true);
        }
      }
    } finally {
      setIsLoading(false);
      
      // Mantener la animación por un momento después de completar la acción
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={`${
        isFavorite ? 'text-red-500 hover:text-red-600' : 'text-gray-500 hover:text-gray-600'
      } transition-colors duration-200`}
      onClick={handleFavoriteClick}
      disabled={isLoading}
      title={`${isFavorite ? 'Eliminar de favoritos' : 'Agregar a favoritos'}`}
    >
      <Heart
        className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''} ${
          isLoading ? 'animate-pulse' : ''
        } ${isAnimating ? 'scale-125 transition-transform' : 'transition-transform'}`}
      />
      <span className="sr-only">
        {isFavorite ? 'Eliminar de favoritos' : 'Agregar a favoritos'}
      </span>
    </Button>
  );
} 